import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUp, Star, WifiOff } from "lucide-react";
import "./common.css";

/* ------------------------------------------------------------- StarRating */
export const StarRating = ({ value = 0, onChange, size = 20, label = "Rating" }) => {
  const readOnly = !onChange;
  return (
    <div
      className={`stars ${readOnly ? "readonly" : ""}`}
      role={readOnly ? "img" : "radiogroup"}
      aria-label={readOnly ? `${label}: ${value} of 5` : label}
    >
      {[1, 2, 3, 4, 5].map((n) =>
        readOnly ? (
          <Star key={n} size={size} className={n <= value ? "on" : ""} aria-hidden="true" />
        ) : (
          <button
            key={n}
            type="button"
            role="radio"
            aria-checked={value === n}
            aria-label={`${n} star${n > 1 ? "s" : ""}`}
            className={`star-btn ${n <= value ? "on" : ""}`}
            onClick={() => onChange(value === n ? 0 : n)}
          >
            <Star size={size} />
          </button>
        )
      )}
    </div>
  );
};

/* ------------------------------------------------------------- EmptyState */
export const EmptyState = ({ icon: Icon, title, text, action, secondary, compact }) => (
  <div className={`empty-panel ${compact ? "compact" : ""}`} role="status">
    {Icon && (
      <div className="empty-panel-icon" aria-hidden="true">
        <Icon size={30} />
      </div>
    )}
    <h2>{title}</h2>
    {text && <p>{text}</p>}
    <div className="empty-panel-actions">
      {action &&
        (action.to ? (
          <Link className="empty-btn primary" to={action.to}>
            {action.label}
          </Link>
        ) : (
          <button className="empty-btn primary" onClick={action.onClick}>
            {action.label}
          </button>
        ))}
      {secondary && (
        <button className="empty-btn" onClick={secondary.onClick}>
          {secondary.label}
        </button>
      )}
    </div>
  </div>
);

/* -------------------------------------------------------------- BackToTop */
export const BackToTop = () => {
  const [show, setShow] = useState(false);

  useEffect(() => {
    const onScroll = () => setShow(window.scrollY > 700);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <button
      className={`back-to-top ${show ? "show" : ""}`}
      aria-label="Back to top"
      tabIndex={show ? 0 : -1}
      onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
    >
      <ArrowUp size={20} />
    </button>
  );
};

/* ---------------------------------------------------------- OfflineBanner */
export const OfflineBanner = () => {
  const [online, setOnline] = useState(typeof navigator === "undefined" ? true : navigator.onLine);

  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);

  if (online) return null;
  return (
    <div className="offline-banner" role="status">
      <WifiOff size={16} aria-hidden="true" />
      You&apos;re offline. Saved recipes and your lists still work.
    </div>
  );
};

/* --------------------------------------------------------- usePwaInstall */
export function usePwaInstall() {
  const [promptEvent, setPromptEvent] = useState(null);

  useEffect(() => {
    const onPrompt = (e) => {
      e.preventDefault();
      setPromptEvent(e);
    };
    const onInstalled = () => setPromptEvent(null);
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const install = async () => {
    if (!promptEvent) return;
    promptEvent.prompt();
    await promptEvent.userChoice;
    setPromptEvent(null);
  };

  return { canInstall: !!promptEvent, install };
}
